import React from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  Code,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
  HStack,
  Link,
  Divider,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';

function ApiDocs() {
  // Get current tenant info from localStorage
  const instanceId = localStorage.getItem('instance_id');
  const instanceName = localStorage.getItem('instance_name');

  const getMethodColor = (method) => {
    const colors = {
      GET: 'green',
      POST: 'blue',
      PUT: 'orange',
      PATCH: 'orange',
      DELETE: 'red',
    };
    return colors[method] || 'gray';
  };

  const adminEndpoints = [
    {
      category: 'Authentication',
      items: [
        { method: 'POST', path: '/api/v1/admin-ui/auth/login/', description: 'Authenticate and get token (super admin only)' },
      ],
    },
    {
      category: 'Users',
      items: [
        { method: 'GET', path: '/api/v1/admin-ui/users/', description: 'List all users and profiles' },
        { method: 'POST', path: '/api/v1/admin-ui/users/', description: 'Create a new user' },
        { method: 'PUT', path: '/api/v1/admin-ui/users/{id}/', description: 'Update a user' },
        { method: 'DELETE', path: '/api/v1/admin-ui/users/{id}/', description: 'Delete a user' },
        { method: 'POST', path: '/api/v1/admin-ui/users/{id}/set-password/', description: 'Set user password' },
        { method: 'POST', path: '/api/v1/admin-ui/users/{id}/promote-super-admin/', description: 'Promote user to super admin' },
      ],
    },
    {
      category: 'Profile Tags',
      items: [
        { method: 'GET', path: '/api/v1/admin-ui/profile-tags/', description: 'List all profile tags' },
        { method: 'POST', path: '/api/v1/admin-ui/profile-tags/', description: 'Create a new tag' },
        { method: 'PUT', path: '/api/v1/admin-ui/profile-tags/{id}/', description: 'Update a tag' },
        { method: 'DELETE', path: '/api/v1/admin-ui/profile-tags/{id}/', description: 'Delete a tag' },
      ],
    },
    {
      category: 'Super Admin & Stats',
      items: [
        { method: 'GET', path: '/api/v1/admin-ui/super-admins/', description: 'List all super admins' },
        { method: 'POST', path: '/api/v1/admin-ui/super-admins/', description: 'Create a new super admin' },
        { method: 'DELETE', path: '/api/v1/admin-ui/super-admins/{id}/', description: 'Remove super admin status' },
        { method: 'GET', path: '/api/v1/admin-ui/stats/', description: 'Get instance statistics' },
      ],
    },
    {
      category: 'API Keys',
      items: [
        { method: 'GET', path: '/api/v1/admin-ui/api-keys/', description: 'List all API keys' },
        { method: 'POST', path: '/api/v1/admin-ui/api-keys/', description: 'Create a new API key' },
        { method: 'PUT', path: '/api/v1/admin-ui/api-keys/{id}/', description: 'Update an API key' },
        { method: 'DELETE', path: '/api/v1/admin-ui/api-keys/{id}/', description: 'Delete an API key' },
        { method: 'POST', path: '/api/v1/admin-ui/api-keys/{id}/regenerate/', description: 'Regenerate an API key' },
      ],
    },
    {
      category: 'Rate Limits',
      items: [
        { method: 'GET', path: '/api/v1/admin-ui/rate-limits/', description: 'List all active rate-limited clients' },
        { method: 'GET', path: '/api/v1/admin-ui/rate-limits/{client_id}/', description: 'Get rate limit status for a specific client' },
        { method: 'POST', path: '/api/v1/admin-ui/rate-limits/{client_id}/reset/', description: 'Reset rate limit counters for a client' },
      ],
    },
    {
      category: 'Redis',
      items: [
        { method: 'GET', path: '/api/v1/admin-ui/redis/status/', description: 'Get Redis server info, memory, key stats and latency' },
        { method: 'POST', path: '/api/v1/admin-ui/redis/flush/', description: 'Flush rate-limit keys or all keys (body: {"type": "rate_limits"|"all"})' },
        { method: 'GET', path: '/api/v1/admin-ui/redis/keys/', description: 'List keys with SCAN (params: pattern, page)' },
        { method: 'GET', path: '/api/v1/admin-ui/redis/key/', description: 'Get value of a specific key (param: key)' },
      ],
    },
    {
      category: 'PostgreSQL',
      items: [
        { method: 'GET', path: '/api/v1/admin-ui/postgres/status/', description: 'Get server version, uptime, database size, connection stats and config' },
        { method: 'GET', path: '/api/v1/admin-ui/postgres/tables/', description: 'List tables with sizes, row counts, vacuum stats (params: page, sort, search)' },
        { method: 'GET', path: '/api/v1/admin-ui/postgres/activity/', description: 'Get active queries and connections from pg_stat_activity' },
        { method: 'POST', path: '/api/v1/admin-ui/postgres/query/', description: 'Execute a read-only SQL query (body: {"sql": "SELECT ..."})' },
      ],
    },
    {
      category: 'Elasticsearch',
      items: [
        { method: 'GET', path: '/api/v1/admin-ui/elasticsearch/status/', description: 'Get cluster health, node info, shard counts and index summary' },
        { method: 'GET', path: '/api/v1/admin-ui/elasticsearch/indices/', description: 'List indices with doc counts, sizes and search/indexing stats (params: page, sort, search)' },
        { method: 'POST', path: '/api/v1/admin-ui/elasticsearch/query/', description: 'Execute an ES API request (body: {"path": "/_search", "method": "GET", "body": {...}})' },
      ],
    },
    {
      category: 'Insights',
      items: [
        { method: 'GET', path: '/api/v1/admin-ui/analytics/', description: 'Usage analytics — time-series data for profiles, events, registrations, emails, webhooks, user activity. Supports ?days= param (default 30, max 365). Tenant-scoped.' },
        { method: 'GET', path: '/api/v1/admin-ui/health/', description: 'Health check — verifies database, Redis, Elasticsearch connectivity and data summary. Returns overall status (healthy/degraded/unhealthy).' },
      ],
    },
  ];

  return (
    <Box>
      <Heading size="lg" mb={2}>API Documentation</Heading>
      <Text color="gray.600" mb={6}>
        Complete guide to integrating with the Selwyn API.
      </Text>

      {/* Public API Section - Primary */}
      <Box bg="white" borderRadius="lg" boxShadow="md" overflow="hidden" mb={8}>
        <Box bg="orange.600" color="white" p={6}>
          <Heading size="md" mb={2}>🔑 Public API</Heading>
          <Text opacity={0.9}>
            Create users, manage profiles, and assign tags using API keys. Perfect for integrations with external systems.
          </Text>
        </Box>
        
        <Box p={6}>
          {/* Current Tenant Info */}
          <Alert status="info" mb={6} borderRadius="md">
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">Your Current Tenant</Text>
              <Text>
                Tenant ID: <Code fontWeight="bold">{instanceId || 'Not logged in'}</Code>
                {instanceName && <> — {instanceName}</>}
              </Text>
              <Text fontSize="sm" mt={1}>
                Each API key is bound to a specific tenant. Users created via the API will be added to the tenant associated with the API key.
              </Text>
            </Box>
          </Alert>

          {/* Authentication */}
          <Heading size="sm" mb={4}>Authentication</Heading>
          <Text mb={4}>
            All Public API requests require an API key passed in the <Code>X-API-Key</Code> header.
            Create and manage API keys in the{' '}
            <Link href="/api-keys" color="orange.600" fontWeight="bold">API Keys</Link> section.
          </Text>
          <Text mb={4} fontSize="sm" color="gray.600">
            <strong>Note:</strong> Each API key is tied to a specific tenant (instance). The tenant is automatically 
            determined by the API key you use — you do not need to pass a tenant ID header.
          </Text>
          <Code p={4} display="block" borderRadius="md" bg="gray.100" whiteSpace="pre">
{`curl -X POST https://your-domain/api/v1/public/users/create/ \\
  -H "X-API-Key: your-api-key-here" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "John Doe", "email": "john@example.com"}'`}
          </Code>

          <Divider my={6} />

          {/* Permissions */}
          <Heading size="sm" mb={4}>API Key Permissions</Heading>
          <Table variant="simple" size="sm" mb={6}>
            <Thead>
              <Tr>
                <Th>Permission</Th>
                <Th>Allows</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr><Td><Badge colorScheme="green">can_create_users</Badge></Td><Td>Create new users with profiles</Td></Tr>
              <Tr><Td><Badge colorScheme="blue">can_read_users</Badge></Td><Td>List and view users, lookup by email</Td></Tr>
              <Tr><Td><Badge colorScheme="yellow">can_update_users</Badge></Td><Td>Update user profiles, add/remove tags</Td></Tr>
              <Tr><Td><Badge colorScheme="red">can_delete_users</Badge></Td><Td>Delete users and their profiles</Td></Tr>
              <Tr><Td><Badge colorScheme="purple">can_manage_tags</Badge></Td><Td>Create new profile tags</Td></Tr>
            </Tbody>
          </Table>

          <Divider my={6} />

          {/* Endpoints with Examples */}
          <Heading size="sm" mb={4}>Endpoints</Heading>
          
          <Tabs variant="enclosed" colorScheme="orange">
            <TabList>
              <Tab>Create User</Tab>
              <Tab>List Users</Tab>
              <Tab>Get User</Tab>
              <Tab>Update User</Tab>
              <Tab>Delete User</Tab>
              <Tab>Tags</Tab>
            </TabList>

            <TabPanels>
              {/* Create User */}
              <TabPanel>
                <HStack mb={4}>
                  <Badge colorScheme="blue" fontSize="md" px={3} py={1}>POST</Badge>
                  <Code fontSize="md">/api/v1/public/users/create/</Code>
                </HStack>
                <Text mb={4}>Create a new user with profile. Users can be pre-tagged during creation.</Text>
                <Text fontWeight="bold" mb={2}>Request Body:</Text>
                <Code p={4} display="block" borderRadius="md" bg="gray.100" whiteSpace="pre" mb={4}>
{`{
  "name": "John Doe",          // required
  "email": "john@example.com", // required
  "username": "johndoe",       // optional, auto-generated if not provided
  "password": "securepass123", // optional, auto-generated if not provided
  "tags": ["VIP", "Partner"],  // optional, tag names to assign
  "company_name": "Acme Inc",  // optional
  "job_title": "Developer",    // optional
  "mobile": "+1234567890",     // optional
  "description": "A brief bio" // optional
}`}
                </Code>
                <Text fontWeight="bold" mb={2}>Example Request:</Text>
                <Code p={4} display="block" borderRadius="md" bg="gray.800" color="green.300" whiteSpace="pre" mb={4}>
{`curl -X POST https://your-domain/api/v1/public/users/create/ \\
  -H "X-API-Key: abc123..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Jane Smith",
    "email": "jane@company.com",
    "tags": ["Premium", "Newsletter"],
    "company_name": "Tech Corp"
  }'`}
                </Code>
                <Text fontWeight="bold" mb={2}>Response (201 Created):</Text>
                <Code p={4} display="block" borderRadius="md" bg="gray.100" whiteSpace="pre">
{`{
  "success": true,
  "user": {
    "id": 123,
    "name": "Jane Smith",
    "email": "jane@company.com",
    "username": "jane",
    "tags": ["Premium", "Newsletter"]
  },
  "message": "User created successfully"
}`}
                </Code>
              </TabPanel>

              {/* List Users */}
              <TabPanel>
                <HStack mb={4}>
                  <Badge colorScheme="green" fontSize="md" px={3} py={1}>GET</Badge>
                  <Code fontSize="md">/api/v1/public/users/</Code>
                </HStack>
                <Text mb={4}>List all users with optional filtering.</Text>
                <Text fontWeight="bold" mb={2}>Query Parameters:</Text>
                <Table variant="simple" size="sm" mb={4}>
                  <Thead>
                    <Tr>
                      <Th>Parameter</Th>
                      <Th>Description</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    <Tr><Td><Code>tag</Code></Td><Td>Filter by tag name (exact match)</Td></Tr>
                    <Tr><Td><Code>email</Code></Td><Td>Filter by email (partial match)</Td></Tr>
                    <Tr><Td><Code>limit</Code></Td><Td>Maximum results (default 100, max 1000)</Td></Tr>
                    <Tr><Td><Code>offset</Code></Td><Td>Pagination offset (default 0)</Td></Tr>
                  </Tbody>
                </Table>
                <Text fontWeight="bold" mb={2}>Example Request:</Text>
                <Code p={4} display="block" borderRadius="md" bg="gray.800" color="green.300" whiteSpace="pre" mb={4}>
{`curl "https://your-domain/api/v1/public/users/?tag=VIP&limit=50" \\
  -H "X-API-Key: abc123..."`}
                </Code>
                <Text fontWeight="bold" mb={2}>Response:</Text>
                <Code p={4} display="block" borderRadius="md" bg="gray.100" whiteSpace="pre">
{`{
  "success": true,
  "users": [
    {
      "id": 123,
      "name": "Jane Smith",
      "email": "jane@company.com",
      "username": "jane",
      "company_name": "Tech Corp",
      "job_title": "Developer",
      "active": true,
      "tags": ["VIP", "Newsletter"],
      "created_at": "2024-01-15T10:30:00Z"
    }
  ],
  "pagination": {
    "total": 150,
    "limit": 50,
    "offset": 0
  }
}`}
                </Code>
              </TabPanel>

              {/* Get User */}
              <TabPanel>
                <HStack mb={4}>
                  <Badge colorScheme="green" fontSize="md" px={3} py={1}>GET</Badge>
                  <Code fontSize="md">/api/v1/public/users/{'{id}'}/ or /api/v1/public/users/lookup/?email=...</Code>
                </HStack>
                <Text mb={4}>Get a single user by ID or look up by email.</Text>
                
                <Text fontWeight="bold" mb={2}>By ID:</Text>
                <Code p={4} display="block" borderRadius="md" bg="gray.800" color="green.300" whiteSpace="pre" mb={4}>
{`curl "https://your-domain/api/v1/public/users/123/" \\
  -H "X-API-Key: abc123..."`}
                </Code>
                
                <Text fontWeight="bold" mb={2}>By Email (Lookup):</Text>
                <Code p={4} display="block" borderRadius="md" bg="gray.800" color="green.300" whiteSpace="pre" mb={4}>
{`curl "https://your-domain/api/v1/public/users/lookup/?email=jane@company.com" \\
  -H "X-API-Key: abc123..."`}
                </Code>
                
                <Text fontWeight="bold" mb={2}>Response:</Text>
                <Code p={4} display="block" borderRadius="md" bg="gray.100" whiteSpace="pre">
{`{
  "success": true,
  "user": {
    "id": 123,
    "name": "Jane Smith",
    "email": "jane@company.com",
    "username": "jane",
    "company_name": "Tech Corp",
    "job_title": "Developer",
    "mobile": "+1234567890",
    "description": "Full stack developer",
    "active": true,
    "blocked": false,
    "tags": ["VIP", "Newsletter"],
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-20T14:45:00Z"
  }
}`}
                </Code>
              </TabPanel>

              {/* Update User */}
              <TabPanel>
                <HStack mb={4}>
                  <Badge colorScheme="orange" fontSize="md" px={3} py={1}>PUT/PATCH</Badge>
                  <Code fontSize="md">/api/v1/public/users/{'{id}'}/update/</Code>
                </HStack>
                <Text mb={4}>Update a user's profile. All fields are optional.</Text>
                <Text fontWeight="bold" mb={2}>Request Body (all optional):</Text>
                <Code p={4} display="block" borderRadius="md" bg="gray.100" whiteSpace="pre" mb={4}>
{`{
  "name": "Jane Doe",
  "email": "jane.new@company.com",
  "company_name": "New Company",
  "job_title": "Senior Developer",
  
  // Tag management options (use only one):
  "tags": ["VIP", "Partner"],  // Replace all tags
  "add_tags": ["NewTag"],      // Add to existing tags
  "remove_tags": ["OldTag"],   // Remove specific tags
  
  "active": true,
  "blocked": false
}`}
                </Code>
                <Text fontWeight="bold" mb={2}>Example - Add a tag to existing user:</Text>
                <Code p={4} display="block" borderRadius="md" bg="gray.800" color="green.300" whiteSpace="pre" mb={4}>
{`curl -X PATCH "https://your-domain/api/v1/public/users/123/update/" \\
  -H "X-API-Key: abc123..." \\
  -H "Content-Type: application/json" \\
  -d '{"add_tags": ["Verified"]}'`}
                </Code>
                <Text fontWeight="bold" mb={2}>Response:</Text>
                <Code p={4} display="block" borderRadius="md" bg="gray.100" whiteSpace="pre">
{`{
  "success": true,
  "user": {
    "id": 123,
    "name": "Jane Smith",
    "email": "jane@company.com",
    "tags": ["VIP", "Newsletter", "Verified"]
  },
  "message": "User updated successfully"
}`}
                </Code>
              </TabPanel>

              {/* Delete User */}
              <TabPanel>
                <HStack mb={4}>
                  <Badge colorScheme="red" fontSize="md" px={3} py={1}>DELETE</Badge>
                  <Code fontSize="md">/api/v1/public/users/{'{id}'}/delete/</Code>
                </HStack>
                <Alert status="warning" mb={4}>
                  <AlertIcon />
                  This permanently deletes the user and their profile. This action cannot be undone.
                </Alert>
                <Text fontWeight="bold" mb={2}>Example Request:</Text>
                <Code p={4} display="block" borderRadius="md" bg="gray.800" color="green.300" whiteSpace="pre" mb={4}>
{`curl -X DELETE "https://your-domain/api/v1/public/users/123/delete/" \\
  -H "X-API-Key: abc123..."`}
                </Code>
                <Text fontWeight="bold" mb={2}>Response:</Text>
                <Code p={4} display="block" borderRadius="md" bg="gray.100" whiteSpace="pre">
{`{
  "success": true,
  "message": "User deleted successfully"
}`}
                </Code>
              </TabPanel>

              {/* Tags */}
              <TabPanel>
                <VStack align="stretch" spacing={6}>
                  <Box>
                    <HStack mb={2}>
                      <Badge colorScheme="green" fontSize="md" px={3} py={1}>GET</Badge>
                      <Code fontSize="md">/api/v1/public/tags/</Code>
                    </HStack>
                    <Text mb={2}>List all available tags for this instance.</Text>
                    <Code p={4} display="block" borderRadius="md" bg="gray.800" color="green.300" whiteSpace="pre" mb={2}>
{`curl "https://your-domain/api/v1/public/tags/" \\
  -H "X-API-Key: abc123..."`}
                    </Code>
                    <Code p={4} display="block" borderRadius="md" bg="gray.100" whiteSpace="pre">
{`{
  "success": true,
  "tags": [
    {"id": 1, "name": "VIP", "description": "VIP customers"},
    {"id": 2, "name": "Newsletter", "description": "Subscribed to newsletter"}
  ]
}`}
                    </Code>
                  </Box>
                  
                  <Divider />
                  
                  <Box>
                    <HStack mb={2}>
                      <Badge colorScheme="blue" fontSize="md" px={3} py={1}>POST</Badge>
                      <Code fontSize="md">/api/v1/public/tags/create/</Code>
                    </HStack>
                    <Text mb={2}>Create a new tag. Requires <Code>can_manage_tags</Code> permission.</Text>
                    <Code p={4} display="block" borderRadius="md" bg="gray.800" color="green.300" whiteSpace="pre">
{`curl -X POST "https://your-domain/api/v1/public/tags/create/" \\
  -H "X-API-Key: abc123..." \\
  -H "Content-Type: application/json" \\
  -d '{"name": "Premium", "description": "Premium subscribers"}'`}
                    </Code>
                  </Box>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>

          <Divider my={6} />

          {/* Error Responses */}
          <Heading size="sm" mb={4}>Error Responses</Heading>
          <Text mb={4}>All errors return a JSON response with <Code>success: false</Code>:</Text>
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Status Code</Th>
                <Th>Meaning</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr><Td><Code>400</Code></Td><Td>Bad Request - Missing or invalid parameters</Td></Tr>
              <Tr><Td><Code>401</Code></Td><Td>Unauthorized - Invalid or missing API key</Td></Tr>
              <Tr><Td><Code>403</Code></Td><Td>Forbidden - API key lacks required permission</Td></Tr>
              <Tr><Td><Code>404</Code></Td><Td>Not Found - User or resource not found</Td></Tr>
              <Tr><Td><Code>405</Code></Td><Td>Method Not Allowed - Wrong HTTP method</Td></Tr>
              <Tr><Td><Code>409</Code></Td><Td>Conflict - Email already exists</Td></Tr>
              <Tr><Td><Code>500</Code></Td><Td>Internal Server Error</Td></Tr>
            </Tbody>
          </Table>
        </Box>
      </Box>

      {/* Admin UI API Section - Secondary */}
      <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
        <Box bg="gray.600" color="white" p={4}>
          <Heading size="sm">Admin UI API</Heading>
          <Text fontSize="sm" opacity={0.9}>Internal endpoints for the admin interface (Token-based auth)</Text>
        </Box>
        
        <Box p={4}>
          <Alert status="info" mb={4}>
            <AlertIcon />
            These endpoints are used internally by this admin interface. They require token authentication from login.
          </Alert>
          
          <Accordion allowMultiple>
            {adminEndpoints.map((category, idx) => (
              <AccordionItem key={idx} border="none" mb={2}>
                <AccordionButton
                  bg="gray.50"
                  borderRadius="md"
                  _hover={{ bg: 'gray.100' }}
                  _expanded={{ borderBottomRadius: 0 }}
                >
                  <Box flex="1" textAlign="left">
                    <Text fontWeight="medium">{category.category}</Text>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel bg="gray.50" borderBottomRadius="md">
                  <VStack align="stretch" spacing={2}>
                    {category.items.map((endpoint, endpointIdx) => (
                      <HStack key={endpointIdx} p={2} bg="white" borderRadius="md" fontSize="sm">
                        <Badge colorScheme={getMethodColor(endpoint.method)} minW="50px" textAlign="center" fontSize="xs">
                          {endpoint.method}
                        </Badge>
                        <Code flex="1" bg="transparent" fontSize="xs">{endpoint.path}</Code>
                        <Text fontSize="xs" color="gray.600">{endpoint.description}</Text>
                      </HStack>
                    ))}
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Box>
      </Box>

      <Box bg="orange.50" p={6} borderRadius="lg" mt={6}>
        <Heading size="sm" color="orange.800" mb={2}>Need more help?</Heading>
        <Text color="orange.700">
          For the full Django REST Framework API browser, visit{' '}
          <Link href="/api/" color="orange.600" fontWeight="bold" isExternal>
            /api/
          </Link>
        </Text>
      </Box>
    </Box>
  );
}

export default ApiDocs;
